import { createTheme, ThemeOptions } from '@mui/material/styles';
import createPalette from '@mui/material/styles/createPalette';
import { CustomColorOverrides, CustomPalette, CustomPaletteOptions } from './types';

declare module '@mui/material/styles' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface Palette extends CustomPalette {}
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface PaletteOptions extends CustomPaletteOptions {}
}
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    h1Heavy: true;
    h2Heavy: true;
    h3Heavy: true;
    h4Heavy: true;
    h5Heavy: true;
    body3: true;
    body4: true;
    body1Heavy: true;
    body2Heavy: true;
    body3Heavy: true;
    body4Heavy: true;
    captionHeavy: true;
    subtitle1Heavy: true;
    subtitle2Heavy: true;
    // | 'h1'
    // | 'h2'
    // | 'h3'
    // | 'h4'
    // | 'h5'
    // | 'h6'
    // | 'subtitle1'
    // | 'subtitle2'
    // | 'body1'
    // | 'body2'
    // | 'caption'
    // | 'button'
    // | 'overline'
    // | 'inherit'
  }
}
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides extends CustomColorOverrides {}
}

export const FONT_GSS_FUTURA = 'GSS Futura';
export const FONT_GSS_PRINT_WIDE = 'GSS Print Wide';
export const FONT_GSS_CURSIVE_BEG_WIDE = 'GSS Cursive Beginner Wide';

const palette = createPalette({
  primary: {
    main: '#DB1F64',
    light: '#E24C83',
    lighter: '#f3a8c1',
    dark: '#AF1950',
    contrastText: '#FFF'
  },
  secondary: {
    main: '#006DB8',
    light: '#338AC6',
    lighter: '#a1c7e4',
    dark: '#005793',
    contrastText: '#FFF'
  },
  error: {
    main: '#D12348',
    light: '#FF829D',
    lightest: '#FFE7EC',
    contrastText: '#FFF'
  },
  warning: {
    main: '#E56A2F',
    light: '#FFA579',
    lightest: '#FFDECE',
    contrastText: '#FFF'
  },
  success: {
    main: '#74B23B',
    light: '#C5EF9F',
    lightest: '#EBFFD8',
    contrastText: '#FFF'
  },
  black: {
    main: '#000000',
    light: '#202020'
  },
  link: {
    main: '#2878C1',
    darker: '#194C7B',
    light: '#83B8E8',
    lighter: '#E3F1FF',
    lightest: '#F9FCFF'
  },
  grey: {
    main: '#6A6A6A',
    light: '#A6A6A6',
    lighter: '#DBDBDB',
    lightest: '#F4F4F4'
  },
  kinder: {
    darker: '#C26714',
    main: '#FA9132',
    light: '#F2B984',
    lighter: '#FFF2E6'
  },
  kickKinder: {
    main: '#A46BBA',
    darker: '#68367C',
    light: '#E0A6F7',
    lighter: '#F8E6FF'
  },
  first: {
    main: '#FCE852',
    darker: '#C9AB36',
    light: '#FFF5AB',
    lighter: '#FFFDEB'
  },
  second: {
    main: '#27AFA7',
    darker: '#14605B',
    light: '#97E9E4',
    lighter: '#F3FFFE'
  },
  third: {
    main: '#FB8388',
    darker: '#B35054',
    light: '#FFA5A9',
    lighter: '#FFEEEE'
  },
  fourth: {
    main: '#5E90D8',
    darker: '#3C6092',
    light: '#9FC2F4',
    lighter: '#EDF5FF'
  }
} as any);

export const baseTheme = createTheme({
  palette,
  breakpoints: {
    values: {
      xs: 0,
      sm: 850,
      md: 960,
      lg: 1280,
      xl: 1920
    }
  }
} as any);

export const theme = {
  ...baseTheme,
  constants: {
    modals: {
      footerHeight: '80px',
      stepperHeight: '24px'
    }
  },
  typography: {
    fontFamily: FONT_GSS_FUTURA,
    h1: {
      fontFamily: FONT_GSS_FUTURA,
      fontWeight: 800,
      fontStyle: 'normal',
      fontSize: baseTheme.typography.pxToRem(40),
      lineHeight: baseTheme.typography.pxToRem(60)
    },
    h2: {
      fontFamily: FONT_GSS_FUTURA,
      fontWeight: 800,
      fontStyle: 'normal',
      fontSize: baseTheme.typography.pxToRem(32),
      lineHeight: baseTheme.typography.pxToRem(48)
    },
    h3: {
      fontFamily: FONT_GSS_FUTURA,
      fontWeight: 400,
      fontStyle: 'normal',
      fontSize: baseTheme.typography.pxToRem(28),
      lineHeight: baseTheme.typography.pxToRem(42)
    },
    h4: {
      fontFamily: FONT_GSS_FUTURA,
      fontWeight: 600,
      fontStyle: 'normal',
      fontSize: baseTheme.typography.pxToRem(24),
      lineHeight: baseTheme.typography.pxToRem(36)
    },
    h5: {
      fontFamily: FONT_GSS_FUTURA,
      fontWeight: 600,
      fontStyle: 'normal',
      fontSize: baseTheme.typography.pxToRem(20),
      lineHeight: baseTheme.typography.pxToRem(30)
    },
    h6: {
      fontFamily: FONT_GSS_FUTURA,
      fontWeight: 600,
      fontStyle: 'normal',
      fontSize: baseTheme.typography.pxToRem(18),
      lineHeight: baseTheme.typography.pxToRem(27)
    },
    // P-small and table-cell
    body1: {
      fontFamily: FONT_GSS_FUTURA,
      fontStyle: 'normal',
      fontWeight: 400,
      fontSize: baseTheme.typography.pxToRem(14),
      lineHeight: baseTheme.typography.pxToRem(21)
    },
    // Not used for now
    body2: {
      fontFamily: FONT_GSS_FUTURA,
      fontStyle: 'normal',
      fontWeight: 400,
      fontSize: baseTheme.typography.pxToRem(16),
      lineHeight: baseTheme.typography.pxToRem(24)
    },
    // P
    subtitle1: {
      fontFamily: FONT_GSS_FUTURA,
      fontWeight: 400,
      fontStyle: 'normal',
      fontSize: baseTheme.typography.pxToRem(18),
      lineHeight: baseTheme.typography.pxToRem(27)
    },
    // Micro
    subtitle2: {
      fontFamily: FONT_GSS_FUTURA,
      fontWeight: 400,
      fontStyle: 'normal',
      fontSize: baseTheme.typography.pxToRem(12),
      lineHeight: baseTheme.typography.pxToRem(21)
    },
    //Helper
    caption: {
      fontFamily: FONT_GSS_FUTURA,
      fontWeight: 600,
      fontStyle: 'normal',
      fontSize: baseTheme.typography.pxToRem(14),
      lineHeight: baseTheme.typography.pxToRem(21)
    }
  },
  components: {
    MuiOutlinedInput: {
      root: {
        backgroundColor: '#FFFFFF',
        // border: '2px solid #202020',
        borderRadius: 8,
        opacity: 1,
        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
          backgroundColor: '#DBDBDB',
          border: `2px solid #202020`
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          border: `2px solid #2D2D2D`
        },
        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
          border: `2px solid #D12348`
        },
        '&.MuiOutlinedInput-adornedStart': {
          paddingLeft: baseTheme.typography.pxToRem(12),
          '&& svg': {
            fontSize: baseTheme.typography.pxToRem(16),
            lineHeight: baseTheme.typography.pxToRem(24),
            color: baseTheme.palette.grey[700]
          }
        }
      },
      inputMarginDense: {
        paddingTop: 0,
        paddingBottom: 0
      },
      marginDense: {
        padding: 0
      },
      input: {
        padding: `${baseTheme.spacing(0.625)}px ${baseTheme.spacing(1.5)}px`
      },
      notchedOutline: {
        top: 0,
        border: '1px solid #979797',
        '&.Mui-disabled': {
          border: `1px solid #CACACA`
        },
        '& legend': {
          display: 'none',
          height: 0
        }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          width: '20px',
          height: '20px',
          color: '#202020',
          fontFamily: FONT_GSS_FUTURA,
          fontSize: '16px',
          lineHeight: '24px',
          textAlign: 'left',
          marginRight: '4px'
        }
      }
      // '&$disabled': {
      //   border: '2px solid #A6A6A6'
      // }
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          borderRadius: '100%',
          width: '20px',
          height: '20px',
          color: '#202020',
          fontFamily: FONT_GSS_FUTURA,
          fontSize: '16px',
          lineHeight: '24px',
          textAlign: 'left',
          margin: '10px'
        },
        colorSecondary: {
          // mui warning
          // commenting out for now,
          // '&$checked': {
          //   color: '#2878C1'
          // },
          // '&$disabled': {
          //   color: '#A6A6A6'
          // }
        }
        // '&$disabled': {
        //   border: '2px solid #A6A6A6'
        // },
        // '&$checked': {
        //   border: '2px solid #000000'
        // }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          margin: '10px',
          borderRadius: '4px',
          color: '#202020',
          fontFamily: FONT_GSS_FUTURA,
          fontSize: '14px',
          lineHeight: '21px',
          textAlign: 'left',
          width: '1256px'
        },
        message: {},
        standardSuccess: {
          backgroundColor: '#EBFFD8',
          border: '1px solid #83BE4E'
        },
        standardInfo: {
          backgroundColor: '#E3F1FF',
          border: '1px solid #2878C1'
        },
        standardWarning: {
          backgroundColor: '#FFDECE',
          border: '1px solid #E56A2F'
        },
        standardError: {
          backgroundColor: '#FFE7EC',
          border: '1px solid #D12348'
        }
      }
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: {
          color: '#202020',
          fontFamily: FONT_GSS_FUTURA,
          fontSize: '18px',
          lineHeight: '27px',
          textAlign: 'left'
        }
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: '#000000',
          marginLeft: '3px',
          '&.Mui-focused': {
            color: '#000000'
          },
          '&.Mui-error': {
            color: '#000000'
          },
          '&.Mui-disabled': {
            color: '#000000'
          }
        }
      }
    },
    Mui: {
      styleOverrides: {
        '&.error': {
          backgroundColor: '#FFFFFF',
          border: '2px solid #D12348'
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        outlined: {
          fontSize: baseTheme.typography.pxToRem(16),
          lineHeight: baseTheme.typography.pxToRem(24),
          fontFamily: FONT_GSS_FUTURA,
          fontStyle: 'normal',
          fontWeight: 600,
          color: '#000000',
          transform: 'none',
          '&.MuiInputLabel-marginDense': {
            transform: 'none'
          },
          '&.MuiInputLabel-shrink': {
            transform: 'none'
          }
        },
        formControl: {
          '&.MuiInputLabel-outlined': {
            position: 'unset',
            transform: 'none'
          }
        },
        animated: {
          '&.MuiInputLabel-outlined': {
            transition: 'none'
          }
        },
        root: {
          marginBottom: '0px',
          color: '#202020',
          fontFamily: FONT_GSS_FUTURA,
          fontWeight: 600,
          fontSize: '12px',
          lineHeight: '18px',
          textAlign: 'left',
          '&.MuiInputLabel-outlined': {
            '&.error': {
              color: '#000000'
            },
            '&::active': {
              color: '#000000'
            },
            '&::focus': {
              color: '#000000'
            },
            '&.Mui-disabled': {
              color: '#000000',
              opacity: 1
            }
          }
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          color: '#202020',
          fontFamily: FONT_GSS_FUTURA,
          fontSize: '16px',
          lineHeight: baseTheme.typography.pxToRem(24),
          textAlign: 'left',
          height: baseTheme.typography.pxToRem(48),
          minWidth: baseTheme.typography.pxToRem(240)
        },
        selectMenu: {
          '&.MuiSelect-outlined': {
            border: '2px solid #202020',
            borderRadius: '8px'
          }
        },
        select: {
          '&.Mui-disabled': {
            backgroundColor: '#DBDBDB',
            '&:focus': {
              backgroundColor: '#DBDBDB'
            },
            '&:active': {
              backgroundColor: '#DBDBDB'
            }
          },
          '&:focus': {
            backgroundColor: 'transparent'
          }
        }
      }
    },
    MuiInputBase: {
      root: {
        backgroundColor: '#FFFFFF',
        border: '2px solid #202020',
        padding: '5px',
        borderRadius: '8px',
        '&.Mui-focused': {
          backgroundColor: '#FFFFFF',
          border: '2px solid #202020'
        },
        '&.Mui-disabled': {
          backgroundColor: '#DBDBDB'
        },
        '&.Mui-error': {
          border: '2px solid #D12348'
        },
        '&.MuiInput-underline:after': {
          border: 'none'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        contained: {
          textTransform: 'none',
          borderRadius: '8px',
          height: baseTheme.typography.pxToRem(48),
          width: baseTheme.typography.pxToRem(155),
          backgroundColor: '#2878C1',
          fontFamily: FONT_GSS_FUTURA,
          fontSize: baseTheme.typography.pxToRem(16),
          lineHeight: baseTheme.typography.pxToRem(24),
          color: '#FFFFFF',
          textAlign: 'left',
          '&:hover': {
            backgroundColor: '#194C7B'
          },
          '&:active': {
            backgroundColor: '#83B8E8'
          },
          '&:disabled': {
            backgroundColor: '#A6A6A6',
            color: '#FFFFFF',
            border: 'none'
          }
        },
        label: {
          margin: 'auto'
        },
        containedSizeLarge: {
          borderRadius: 12,
          height: baseTheme.typography.pxToRem(67),
          width: baseTheme.typography.pxToRem(182),
          fontSize: baseTheme.typography.pxToRem(18),
          lineHeight: baseTheme.typography.pxToRem(27)
        },
        containedSizeSmall: {
          borderRadius: 4,
          height: baseTheme.typography.pxToRem(37),
          width: baseTheme.typography.pxToRem(104),
          fontSize: baseTheme.typography.pxToRem(14),
          lineHeight: baseTheme.typography.pxToRem(21)
        },
        outlined: {
          textTransform: 'none',
          height: baseTheme.typography.pxToRem(48),
          width: baseTheme.typography.pxToRem(155),
          backgroundColor: '#FFFFFF',
          border: '3px solid #2878C1',
          borderRadius: '8px',
          color: '#2878C1',
          fontFamily: FONT_GSS_FUTURA,
          fontSize: baseTheme.typography.pxToRem(16),
          lineHeight: baseTheme.typography.pxToRem(24),
          textAlign: 'left',
          '&:hover': {
            color: '#194C7B',
            backgroundColor: '#FFFFFF',
            border: '3px solid #194C7B'
          },
          '&:active': {
            color: '#194C7B',
            backgroundColor: '#E3F1FF',
            border: '3px solid #194C7B'
          },
          '&:disabled': {
            backgroundColor: '#FFFFFF',
            border: '3px solid #979797',
            color: '#979797'
          }
        },
        outlinedSizeSmall: {
          borderRadius: 'pxc',
          color: '#2878C1',
          height: baseTheme.typography.pxToRem(37),
          width: baseTheme.typography.pxToRem(104),
          fontSize: baseTheme.typography.pxToRem(14),
          lineHeight: baseTheme.typography.pxToRem(21)
        },
        outlinedSizeLarge: {
          borderRadius: 4,
          color: '#2878C1',
          height: baseTheme.typography.pxToRem(67),
          width: baseTheme.typography.pxToRem(182),
          fontSize: baseTheme.typography.pxToRem(18),
          lineHeight: baseTheme.typography.pxToRem(27)
        },
        containedInherit: {
          borderRadius: '8px',
          height: baseTheme.typography.pxToRem(48),
          width: baseTheme.typography.pxToRem(155),
          backgroundColor: '#2878C1',
          fontFamily: FONT_GSS_FUTURA,
          fontSize: baseTheme.typography.pxToRem(16),
          lineHeight: baseTheme.typography.pxToRem(24),
          color: '#FFFFFF',
          textAlign: 'left',
          '&:hover': {
            backgroundColor: '#194C7B'
          },
          '&:active': {
            backgroundColor: '#83B8E8'
          },
          '&:disabled': {
            backgroundColor: '#A6A6A6',
            color: '#FFFFFF',
            border: 'none'
          }
        },
        containedPrimary: {
          color: '#FFFFFF',
          backgroundColor: '#202020',
          '&:hover': {
            color: '#FFFFFF',
            backgroundColor: '#6A6A6A'
          },
          '&:active': {
            color: '#FFFFFF',
            backgroundColor: '#000000'
          },
          '&:disabled': {
            color: '#FFFFFF',
            backgroundColor: '#A6A6A6'
          }
        },
        containedSecondary: {
          color: '#FFFFFF',
          backgroundColor: '#D12348',
          '&:hover': {
            color: '#FFFFFF',
            backgroundColor: '#B41A3B'
          },
          '&:active': {
            color: '#FFFFFF',
            backgroundColor: '#F3486C'
          },
          '&:disabled': {
            color: '#FFFFFF',
            backgroundColor: '#A6A6A6'
          }
        },
        text: {
          color: '#2878C1',
          fontFamily: FONT_GSS_FUTURA,
          fontSize: '14px',
          lineHeight: '21px',
          textAlign: 'left',
          width: '158px',
          height: '37px',
          '&:hover': {
            color: '#194C7B',
            backgroundColor: '#FFFFFF'
          },
          '&:active': {
            color: '#194C7B',
            backgroundColor: '#E3F1FF'
          },
          '&:disabled': {
            color: '#A6A6A6'
          }
        },
        iconSizeSmall: {
          '& > *:first-of-type': {
            fontSize: baseTheme.typography.pxToRem(14),
            width: '20px',
            height: '20px'
          }
        },
        iconSizeMedium: {
          '& > *:first-of-type': {
            fontSize: baseTheme.typography.pxToRem(16),
            width: '20px',
            height: '20px'
          }
        },
        iconSizeLarge: {
          '& > *:first-of-type': {
            fontSize: baseTheme.typography.pxToRem(18),
            width: '20px',
            height: '20px'
          }
        },
        startIcon: {
          marginRight: baseTheme.spacing(1.5),
          width: '20px',
          height: '20px'
        }
      }
    },
    MuiFormControlLabel: {
      label: {
        marginLeft: 0
      }
    },
    MuiFormHelperText: {
      root: {
        marginTop: 0,
        fontSize: baseTheme.typography.pxToRem(12),
        lineHeight: baseTheme.typography.pxToRem(18),
        fontWeight: 400,
        '&.Mui-error': {
          color: baseTheme.palette.primary.main
        }
      },
      marginDense: {
        marginTop: 0
      },
      contained: {
        color: baseTheme.palette.primary.main,
        marginLeft: 0,
        marginRight: 0
      }
    },
    MuiSwitch: {
      root: {
        width: 48,
        height: 26,
        padding: 0
      },
      colorPrimary: {
        '&.disabled$checked': {
          color: '#ffffff'
        },
        '&.checked': {
          color: 'white',
          border: 'none',
          opacity: 1
        },
        '&.checked + $track': {
          backgroundColor: '#208c41',
          border: 'none',
          opacity: 1
        },
        '&.disabled$checked + $track': {
          backgroundColor: '#8fc5a0'
        },
        '&.disabled + $thumb': {
          color: 'red'
        }
      },
      colorSecondary: {
        '&.disabled': {
          color: '#cacaca'
        },
        '&.disabled$checked': {
          color: '#ffffff'
        },
        '&.checked': {
          color: 'white',
          border: 'none',
          opacity: 1
        },
        '&.disabled + $track': {
          backgroundColor: '#eaeaea',
          borderColor: '#cacaca'
        },
        '&.checked + $track': {
          backgroundColor: '#208c41',
          border: 'none',
          opacity: 1
        },
        '&.disabled$checked + $track': {
          backgroundColor: '#8fc5a0'
        }
      },
      switchBase: {
        padding: 1,
        top: 1,
        left: 3,
        color: '#696969',
        '&.checked': {
          top: 0,
          transform: 'translateX(21px)'
        },
        '&.disabled + .track': {
          opacity: 'unset'
        }
      },
      thumb: {
        marginTop: 2,
        width: 18,
        height: 18,
        boxShadow: 'unset'
      },
      track: {
        height: 24,
        width: 48,
        borderRadius: 24 / 2,
        border: `1px solid #696969`,
        backgroundColor: '#eaeaea',
        opacity: 1,
        transition:
          'background-color 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, border 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
        '&.disabled + .track': {
          backgroundColor: '#eaeaea',
          borderColor: '#cacaca'
        }
      },
      checked: {}
    },
    MuiTabs: {
      indicator: {
        display: 'none'
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          borderRadius: '5px',
          textTransform: 'none',
          margin: baseTheme.spacing(1)
        },
        textColorSecondary: {
          fontWeight: baseTheme.typography.fontWeightRegular,
          fontSize: baseTheme.typography.pxToRem(16),
          lineHeight: baseTheme.typography.pxToRem(24),
          color: baseTheme.palette.grey[800],
          '&.Mui-selected': {
            backgroundColor: baseTheme.palette.secondary.main,
            color: baseTheme.palette.common.white
          }
        }
      }
    }
  }
} as ThemeOptions;

export default createTheme(theme as any);
